import { useRef, useCallback, useEffect } from 'react';

const CHUNK_DURATION_MS = 2 * 60 * 1000; // 2 minutes par chunk
const UPLOAD_ENDPOINT   = '/api/recordings/chunk';

export function useContinuousRecording({ streamRef, enabled, deviceId, onChunkUploaded }) {
  const recorderRef  = useRef(null);
  const chunksRef    = useRef([]);
  const intervalRef  = useRef(null);
  const activeRef    = useRef(false);

  const uploadChunk = useCallback(async (blob, startedAt) => {
    if (!blob || blob.size < 1000) return; // ignorer les chunks vides
    try {
      const form = new FormData();
      form.append('chunk',      blob, `chunk_${Date.now()}.webm`);
      form.append('deviceId',   deviceId);
      form.append('startedAt',  startedAt.toISOString());
      form.append('endedAt',    new Date().toISOString());
      form.append('duration',   Math.round(CHUNK_DURATION_MS / 1000).toString());
      form.append('continuous', 'true');

      const token = localStorage.getItem('elvyra_token');
      const res   = await fetch(UPLOAD_ENDPOINT, {
        method:  'POST',
        headers: { Authorization: `Bearer ${token}` },
        body:    form,
      });
      if (res.ok) onChunkUploaded?.();
    } catch (e) {
      console.warn('[ContinuousRec] Upload failed:', e.message);
    }
  }, [deviceId, onChunkUploaded]);

  const startChunk = useCallback(() => {
    const stream = streamRef.current;
    if (!stream || !activeRef.current) return;

    chunksRef.current = [];
    const startedAt   = new Date();

    const options = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
      ? { mimeType: 'video/webm;codecs=vp9', videoBitsPerSecond: 400000 }
      : { mimeType: 'video/webm',            videoBitsPerSecond: 400000 };

    const recorder = new MediaRecorder(stream, options);
    recorderRef.current = recorder;

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: 'video/webm' });
      uploadChunk(blob, startedAt);
      chunksRef.current = [];
      // Démarrer le chunk suivant si encore actif
      if (activeRef.current) startChunk();
    };

    recorder.start(5000); // collect data toutes les 5s

    // Arrêter après CHUNK_DURATION_MS
    intervalRef.current = setTimeout(() => {
      if (recorder.state === 'recording') recorder.stop();
    }, CHUNK_DURATION_MS);

  }, [streamRef, uploadChunk]);

  useEffect(() => {
    if (enabled && streamRef.current) {
      activeRef.current = true;
      startChunk();
    } else {
      activeRef.current = false;
      clearTimeout(intervalRef.current);
      if (recorderRef.current?.state === 'recording') {
        recorderRef.current.stop();
      }
    }
    return () => {
      activeRef.current = false;
      clearTimeout(intervalRef.current);
      if (recorderRef.current?.state === 'recording') {
        recorderRef.current.stop();
      }
    };
  }, [enabled, startChunk]);
}
